import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton
} from '@mui/material';
import { useSnackbar } from 'notistack';
import cuentaCorrienteProveedoresService, {
  CuentaCorrienteProveedorSaldo,
  ProveedorDeudaPorVencimiento
} from '../../services/cuentaCorrienteProveedoresService';
import rutaService, { Ruta } from '../../services/rutaService';
import ramoProvService, { RamoProv } from '../../services/ramoProvService';
import { formatCurrency } from '../../utils/formatters';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon,
  ListAlt as ListAltIcon,
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  Paid as PaidIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import InformesBackButton from '../../components/shared/InformesBackButton';
import PDFViewer from '../../components/shared/PDFViewer';
import { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';

async function buildListadoDeudaPdfBlob(
  rows: ProveedorDeudaPorVencimiento[],
  opts: { fechaHasta: string; ruta: Ruta | null; ramo: RamoProv | null }
): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  doc.setLineHeightFactor(1);
  const marginX = 40;
  let y = 50;
  doc.setFontSize(18);
  doc.text('Deuda por vencimiento', marginX, y);
  y += 22;
  doc.setFontSize(12);
  const hastaLabel = new Date(`${opts.fechaHasta}T12:00:00`).toLocaleDateString('es-AR', { dateStyle: 'long' });
  doc.text(`Cuentas por pagar — vencimientos hasta ${hastaLabel}`, marginX, y);
  y += 18;
  const partes: string[] = [];
  if (opts.ruta) {
    partes.push(`Ruta: ${opts.ruta.nombreRuta ?? opts.ruta.codigoRuta ?? ''}`);
  }
  if (opts.ramo) {
    partes.push(
      `Ramo: ${(opts.ramo.nombreramoprov || '').trim() || (opts.ramo.codigoramoprov || '').trim()}`
    );
  }
  if (partes.length) {
    doc.text(partes.join('  ·  '), marginX, y);
    y += 16;
  }
  doc.setFontSize(10);
  doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, y);
  y += 20;

  const totalGeneral = rows.reduce(
    (sum, r) => sum + (Number(r.deudaHastaVencimiento) || 0),
    0
  );

  const cellPadBody = { top: 0, right: 2, bottom: 0, left: 2 };
  const cellPadHead = { top: 1, right: 2, bottom: 1, left: 2 };

  autoTable(doc, {
    startY: y,
    theme: 'striped',
    head: [['Nro.', 'Proveedor', 'Ruta', 'Ramo', 'Deuda']],
    body: rows.map((r) => [
      String(r.nroProveedor),
      r.nombre,
      (r.nombreRuta ?? r.codigoRuta ?? '—') as string,
      (r.nombreRamoProv ?? r.codigoRamoProv) as string,
      formatCurrency(r.deudaHastaVencimiento)
    ]),
    ...(rows.length
      ? {
          foot: [
            [
              {
                content: `Totales — ${rows.length} fila${rows.length === 1 ? '' : 's'}`,
                colSpan: 4,
                styles: { halign: 'right', fontStyle: 'bold' as const }
              },
              {
                content: formatCurrency(totalGeneral),
                styles: { halign: 'right' as const, fontStyle: 'bold' as const }
              }
            ]
          ],
          footStyles: {
            fillColor: [240, 245, 250] as [number, number, number],
            textColor: [0, 0, 0] as [number, number, number],
            fontSize: 7.5,
            cellPadding: { top: 2, right: 2, bottom: 2, left: 2 }
          },
          showFoot: 'lastPage' as const
        }
      : { showFoot: 'never' as const }),
    margin: { left: marginX, right: marginX },
    styles: {
      fontSize: 7,
      cellPadding: cellPadBody,
      overflow: 'linebreak',
      valign: 'top',
      minCellHeight: 0
    },
    headStyles: {
      fillColor: [25, 118, 210],
      textColor: 255,
      fontSize: 7.5,
      cellPadding: cellPadHead,
      lineWidth: 0,
      valign: 'middle'
    },
    bodyStyles: {
      valign: 'top'
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 128 },
      2: { cellWidth: 78 },
      3: { cellWidth: 78 },
      4: { halign: 'right' }
    }
  });

  const totalPaginas = doc.getNumberOfPages();
  const anchoPag = doc.internal.pageSize.getWidth();
  const altoPag = doc.internal.pageSize.getHeight();
  const yPie = altoPag - 18;
  for (let p = 1; p <= totalPaginas; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`Página ${p} de ${totalPaginas}`, anchoPag / 2, yPie, { align: 'center' });
  }
  doc.setTextColor(0, 0, 0);

  return doc.output('blob');
}

/** Claves de listado disponibles (extensible). */
const LISTADOS = [
  {
    id: 'deudaVencida' as const,
    titulo: 'Deuda vencida a fecha',
    descripcion: 'Proveedores con saldo en cuenta corriente por vencimientos hasta una fecha, filtrable por ruta y ramo.'
  }
] as const;

const CuentasPorPagar: React.FC = () => {
  const navigate = useNavigate();

  const [saldos, setSaldos] = useState<CuentaCorrienteProveedorSaldo[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [listadosOpen, setListadosOpen] = useState(false);
  /** null = modal de elección; 'deudaVencida' = formulario de ese listado */
  const [listadoEmitir, setListadoEmitir] = useState<null | 'deudaVencida'>(null);
  const [fechaHastaListado, setFechaHastaListado] = useState(() => new Date().toISOString().slice(0, 10));
  const [rutaFiltro, setRutaFiltro] = useState<Ruta | null>(null);
  const [ramoFiltro, setRamoFiltro] = useState<RamoProv | null>(null);
  const [rutasOptions, setRutasOptions] = useState<Ruta[]>([]);
  const [ramosOptions, setRamosOptions] = useState<RamoProv[]>([]);
  const [loadingCatalogosListado, setLoadingCatalogosListado] = useState(false);
  const [_listadoDeudaRows, setListadoDeudaRows] = useState<ProveedorDeudaPorVencimiento[]>([]);
  const [loadingListadoDeuda, setLoadingListadoDeuda] = useState(false);
  const [exportingListadoDeuda, setExportingListadoDeuda] = useState(false);
  const [listadoDeudaPdfUrl, setListadoDeudaPdfUrl] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!listadosOpen || listadoEmitir !== 'deudaVencida') return;
    let cancelled = false;
    const loadCatalogos = async () => {
      setLoadingCatalogosListado(true);
      try {
        const pageSize = 20;
        const rutas: Ruta[] = [];
        let p = 1;
        while (!cancelled) {
          const r = await rutaService.getRutasPaged({ pageNumber: p, pageSize });
          rutas.push(...r.items);
          if (p >= r.totalPages || r.items.length === 0) break;
          p += 1;
        }
        const ramos: RamoProv[] = [];
        p = 1;
        while (!cancelled) {
          const r = await ramoProvService.getRamosProvPaged({ pageNumber: p, pageSize });
          ramos.push(...r.items);
          if (p >= r.totalPages || r.items.length === 0) break;
          p += 1;
        }
        if (!cancelled) {
          setRutasOptions(rutas);
          setRamosOptions(ramos);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          enqueueSnackbar('No se pudieron cargar rutas o ramos para el listado.', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingCatalogosListado(false);
      }
    };
    void loadCatalogos();
    return () => {
      cancelled = true;
    };
  }, [listadosOpen, listadoEmitir, enqueueSnackbar]);

  const handleCloseListadoDeudaPdf = () => {
    setListadoDeudaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleGenerarListadoDeuda = async () => {
    if (!fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha hasta de vencimiento.', { variant: 'warning' });
      return;
    }
    try {
      setLoadingListadoDeuda(true);
      const rows = await cuentaCorrienteProveedoresService.getDeudaPorVencimiento({
        fechaHasta: fechaHastaListado,
        codigoRuta: rutaFiltro?.codigoRuta ?? undefined,
        codigoRamoProv: ramoFiltro?.codigoramoprov
      });
      setListadoDeudaRows(rows);
      if (!rows.length) {
        enqueueSnackbar('No hay proveedores con deuda en ese corte.', { variant: 'warning' });
        return;
      }
      const blob = await buildListadoDeudaPdfBlob(rows, {
        fechaHasta: fechaHastaListado,
        ruta: rutaFiltro,
        ramo: ramoFiltro
      });
      setListadoDeudaPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setListadoEmitir(null);
      setListadosOpen(false);
      enqueueSnackbar(`PDF generado: ${rows.length} proveedor(es).`, { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo generar el listado de deuda por vencimiento.', { variant: 'error' });
    } finally {
      setLoadingListadoDeuda(false);
    }
  };

  const writeListadoDeudaToExcel = (rows: ProveedorDeudaPorVencimiento[], fechaCorte: string) => {
    const data: Array<Record<string, string | number>> = rows.map((row) => ({
      'Nro. Proveedor': row.nroProveedor,
      Nombre: row.nombre,
      'Cód. Ruta': row.codigoRuta ?? '',
      Ruta: row.nombreRuta ?? '',
      'Cód. Ramo': row.codigoRamoProv,
      Ramo: row.nombreRamoProv ?? '',
      'Deuda venc. hasta fecha': row.deudaHastaVencimiento
    }));
    if (rows.length) {
      const totalGeneral = rows.reduce(
        (sum, r) => sum + (Number(r.deudaHastaVencimiento) || 0),
        0
      );
      data.push({
        'Nro. Proveedor': '',
        Nombre: `Total de filas: ${rows.length}`,
        'Cód. Ruta': '',
        Ruta: '',
        'Cód. Ramo': '',
        Ramo: '',
        'Deuda venc. hasta fecha': ''
      });
      data.push({
        'Nro. Proveedor': '',
        Nombre: 'Total general',
        'Cód. Ruta': '',
        Ruta: '',
        'Cód. Ramo': '',
        Ramo: '',
        'Deuda venc. hasta fecha': totalGeneral
      });
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DeudaPorVencimiento');
    const slug = fechaCorte.replace(/-/g, '');
    XLSX.writeFile(workbook, `deuda_proveedores_por_vencimiento_${slug}.xlsx`);
  };

  const handleExportListadoDeudaExcel = async () => {
    if (!fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha hasta de vencimiento para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingListadoDeuda(true);
      const rows = await cuentaCorrienteProveedoresService.getDeudaPorVencimiento({
        fechaHasta: fechaHastaListado,
        codigoRuta: rutaFiltro?.codigoRuta ?? undefined,
        codigoRamoProv: ramoFiltro?.codigoramoprov
      });
      if (!rows.length) {
        enqueueSnackbar('No hay proveedores con deuda en ese corte para exportar.', { variant: 'warning' });
        return;
      }
      setListadoDeudaRows(rows);
      writeListadoDeudaToExcel(rows, fechaHastaListado);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo exportar el listado a Excel.', { variant: 'error' });
    } finally {
      setExportingListadoDeuda(false);
    }
  };

  const handleCloseListados = () => {
    setListadoEmitir(null);
    setListadosOpen(false);
  };

  const handleOpenListados = () => {
    setListadoEmitir(null);
    setListadoDeudaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setListadosOpen(true);
  };

  // Debounce para la busqueda y carga de datos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const params: {
          pageNumber: number;
          pageSize: number;
          searchTerm?: string;
        } = {
          pageNumber: page + 1,
          pageSize: rowsPerPage
        };

        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        const response = await cuentaCorrienteProveedoresService.getSaldos(params);

        setSaldos(response.items);
        setTotalCount(response.totalCount);
      } catch (error) {
        console.error('Error al obtener las cuentas por pagar:', error);
        enqueueSnackbar('No se pudieron cargar los saldos de proveedores.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const getAllSaldos = async (): Promise<CuentaCorrienteProveedorSaldo[]> => {
    const allItems: CuentaCorrienteProveedorSaldo[] = [];
    const pageSize = 50;
    let pageNumber = 1;
    let hasMore = true;

    const params: {
      pageNumber: number;
      pageSize: number;
      searchTerm?: string;
    } = {
      pageNumber,
      pageSize
    };

    if (searchTerm.trim()) {
      params.searchTerm = searchTerm.trim();
    }

    while (hasMore) {
      const response = await cuentaCorrienteProveedoresService.getSaldos({
        ...params,
        pageNumber
      });

      allItems.push(...response.items);

      const totalPages = Math.ceil(response.totalCount / pageSize);
      hasMore = pageNumber < totalPages;
      pageNumber++;
    }

    return allItems;
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      const allItems = await getAllSaldos();

      if (!allItems.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const data = allItems.map((item) => ({
        'Nro. Proveedor': item.nroProveedor,
        Nombre: item.nombre,
        'Saldo Total': item.saldoTotal,
        'Saldo Vigente': item.saldoVigente,
        'Saldo Vencido': item.saldoVencido
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CuentasPorPagar');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `cuentas_por_pagar_${today}.xlsx`);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      const allItems = await getAllSaldos();

      if (!allItems.length) {
        enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      doc.setFontSize(18);
      doc.text('Cuentas por Pagar', marginX, currentY);
      currentY += 24;

      doc.setFontSize(12);
      const today = new Date();
      doc.text(`Generado: ${today.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 20;

      const autoTable = (await import('jspdf-autotable')).default;

      autoTable(doc, {
        startY: currentY,
        head: [['Proveedor', 'Saldo Total', 'Vigente', 'Vencido']],
        body: allItems.map((row) => [
          `${row.nroProveedor} - ${row.nombre}`,
          formatCurrency(row.saldoTotal),
          formatCurrency(row.saldoVigente),
          formatCurrency(row.saldoVencido)
        ]),
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 10, cellPadding: 6, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255 },
        columnStyles: {
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' }
        }
      });

      const pdfBlob = doc.output('blob');
      const fileName = `cuentas_por_pagar_${today.toISOString().slice(0, 10)}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error: unknown) {
      const err = error as { name?: string };
      if (err?.name === 'AbortError') {
        enqueueSnackbar('Compartir cancelado por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF para compartir:', error);
        enqueueSnackbar('No se pudo generar el PDF para compartir.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && saldos.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            {searchTerm.trim() ? 'No se encontraron resultados para la búsqueda.' : 'No hay proveedores con saldo.'}
          </TableCell>
        </TableRow>
      );
    }

    return saldos.map((row) => (
      <TableRow
        key={row.nroProveedor}
        onClick={() => {
          navigate(`/compras/informes/resumen-cuenta/${row.nroProveedor}`);
        }}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#f9f9f9'
          }
        }}
      >
        <TableCell sx={{ fontWeight: 500 }}>{row.nroProveedor}</TableCell>
        <TableCell>{row.nombre}</TableCell>
        <TableCell>{row.telefono || '-'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {formatCurrency(row.saldoTotal)}
        </TableCell>
        <TableCell align="right">{formatCurrency(row.saldoVigente)}</TableCell>
        <TableCell
          align="right"
          sx={{
            color: row.saldoVencido > 0 ? '#f44336' : 'inherit',
            fontWeight: row.saldoVencido > 0 ? 600 : 'normal'
          }}
        >
          {formatCurrency(row.saldoVencido)}
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ width: '1%', whiteSpace: 'nowrap' }}>
          <IconButton
            size="small"
            color="primary"
            title="Pagar"
            aria-label="Pagar"
            onClick={() => {
              navigate(`/tesoreria/orden-pago/nuevo?nroProveedor=${row.nroProveedor}`);
            }}
            sx={{
              bgcolor: 'primary.main',
              color: 'common.white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <PaidIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  const renderResumenTab = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por número de proveedor o nombre..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#fafafa',
              '&:hover': {
                bgcolor: '#f5f5f5'
              },
              '&.Mui-focused': {
                bgcolor: '#ffffff'
              }
            }
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: 'none',
          border: '1px solid #e0e0e0'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Nro. Proveedor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Teléfono</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Saldo Total
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Saldo Vigente
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Saldo Vencido
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => {
            if (searchTerm.trim()) {
              return `${from}-${to} de ${count} (filtrado)`;
            }
            return `${from}-${to} de ${count}`;
          }}
          sx={{
            borderTop: '1px solid #e0e0e0'
          }}
        />
      </TableContainer>
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InformesBackButton to="/compras/informes" useBrowserBack />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Cuentas por Pagar
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ListAltIcon />}
                onClick={handleOpenListados}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Listados
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)'
                  }
                }}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleShare}
                disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Exportar PDF
              </Button>
            </Stack>
          </Stack>

          {renderResumenTab()}
        </CardContent>
      </Card>

      <Dialog
        open={listadosOpen}
        onClose={handleCloseListados}
        maxWidth={listadoEmitir === 'deudaVencida' ? 'sm' : 'md'}
        fullWidth
      >
        {listadoEmitir == null ? (
          <>
            <DialogTitle>Elegir listado</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccioná qué listado emitir. Luego se abrirá la pantalla con filtros y generación.
              </Typography>
              <Stack spacing={1.5}>
                {LISTADOS.map((opt) => (
                  <Button
                    key={opt.id}
                    fullWidth
                    variant="outlined"
                    onClick={() => setListadoEmitir(opt.id)}
                    startIcon={<DateRangeIcon />}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      borderRadius: 2,
                      borderColor: '#e0e0e0',
                      '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                    }}
                  >
                    <Box sx={{ display: 'block', textAlign: 'left' }}>
                      <Typography component="span" display="block" fontWeight={600} color="text.primary">
                        {opt.titulo}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {opt.descripcion}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : listadoEmitir === 'deudaVencida' ? (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pr: 1
              }}
            >
              <IconButton
                size="small"
                onClick={() => setListadoEmitir(null)}
                aria-label="Volver a listados"
                edge="start"
              >
                <ArrowBackIcon />
              </IconButton>
              <Box component="span" sx={{ flex: 1 }}>
                Deuda vencida a fecha
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Proveedores filtrados por ruta y/o ramo. La deuda es la suma de saldos en cuenta corriente con fecha de
                vencimiento hasta la fecha indicada (solo comprobantes con vencimiento informado). No se listan
                proveedores con deuda 0. Al imprimir, se abre el PDF en el visor integrado; podés volver a abrir
                Listados.
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Fecha hasta (vencimiento)"
                  type="date"
                  value={fechaHastaListado}
                  onChange={(e) => setFechaHastaListado(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={rutasOptions}
                  value={rutaFiltro}
                  onChange={(_, v) => setRutaFiltro(v)}
                  getOptionLabel={(o) => (o?.nombreRuta != null ? `${o.codigoRuta ?? ''} — ${o.nombreRuta}` : '')}
                  isOptionEqualToValue={(a, b) => (a?.codigoRuta ?? null) === (b?.codigoRuta ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Ruta (zona)" placeholder="Todas" size="small" />
                  )}
                  disabled={loadingCatalogosListado}
                />
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={ramosOptions}
                  value={ramoFiltro}
                  onChange={(_, v) => setRamoFiltro(v)}
                  getOptionLabel={(o) =>
                    o?.codigoramoprov != null
                      ? `${(o.codigoramoprov || '').trim()} — ${o.nombreramoprov ?? ''}`
                      : ''
                  }
                  isOptionEqualToValue={(a, b) =>
                    (a?.codigoramoprov?.trim() ?? '') === (b?.codigoramoprov?.trim() ?? '')
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Ramo de proveedor" placeholder="Todos" size="small" />
                  )}
                  disabled={loadingCatalogosListado}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    onClick={() => void handleGenerarListadoDeuda()}
                    disabled={loadingListadoDeuda || loadingCatalogosListado || exportingListadoDeuda}
                    sx={{ textTransform: 'none' }}
                  >
                    Imprimir
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => void handleExportListadoDeudaExcel()}
                    disabled={exportingListadoDeuda || loadingListadoDeuda || loadingCatalogosListado}
                    sx={{ textTransform: 'none' }}
                  >
                    Exportar Excel
                  </Button>
                  {(loadingCatalogosListado || loadingListadoDeuda) && <CircularProgress size={22} />}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {listadoDeudaPdfUrl ? (
        <PDFViewer
          pdfUrl={listadoDeudaPdfUrl}
          onClose={handleCloseListadoDeudaPdf}
          title="Deuda por vencimiento — Cuentas por pagar"
          documentId="deuda-por-vencimiento"
          documentType="informeCliente"
          downloadFileName={`deuda_por_vencimiento_${fechaHastaListado.replace(/-/g, '')}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default CuentasPorPagar;
